/**
 * The rules a company's own edits to its roles must obey (ADR-035).
 *
 * `matrix.ts` is the DEFAULT every company starts from. This file is what a
 * company may and may not do to it, and it lives in the shared package for the
 * usual reason: the API enforces it at the moment of writing, and the screen
 * reads it to decide which boxes to draw as unavailable. A screen offering a
 * tick the server then refuses is worse than not offering it — the admin makes
 * the change, sees it apparently succeed, and finds out weeks later.
 *
 * THREE KINDS OF CELL
 * -------------------
 * `editable`  — the ordinary case. Tick it or do not.
 * `locked`    — always on, cannot be removed. Only the two that make Company
 *               Admin able to undo a mistake.
 * `forbidden` — can never be turned on for this role, whatever anybody ticks.
 *               External roles and money.
 *
 * The last two are not advice. `can()` enforces the external-role rule before
 * it reads any matrix at all, and the locked cells are refused at write time,
 * so neither depends on this file being consulted.
 */

import { PERMISSION_MATRIX } from './matrix.js';
import {
  isExternalRole,
  isFinancialResource,
  type Action,
  type Grant,
  type Resource,
  type Role,
  type RoleGrants,
} from './types.js';

/**
 * Cells Company Admin can never lose.
 *
 * This is the lock-yourself-out guard, and it is the single most likely
 * accident on a screen made of tick boxes: an admin tidying up unticks
 * `role: admin`, and from that moment nobody in the company can edit roles —
 * including to put it back. Recovery needs database access, which a customer
 * does not have.
 *
 * Deliberately only TWO cells, not "everything Company Admin has". The point
 * is that a mistake stays undoable, not that Company Admin is unchangeable: a
 * company that wants its admin out of the snagging register is entitled to
 * that, and can still reach this screen afterwards to change its mind.
 */
export const LOCKED_CELLS: Readonly<Record<string, readonly Action[]>> = Object.freeze({
  role: Object.freeze(['view', 'admin'] as const),
  user: Object.freeze(['view', 'admin'] as const),
});

export function isLockedCell(role: Role, resource: Resource, action: Action): boolean {
  if (role !== 'company_admin') return false;
  return (LOCKED_CELLS[resource] ?? []).includes(action);
}

/**
 * Cells that can never be turned on, whatever the admin ticks.
 *
 * `04` §"External user rules" §4: *"No commercial data, ever. Enforced by role,
 * not by flag."* A client or a subcontractor seeing the contractor's margin is
 * the one permission mistake that is a commercial incident rather than an
 * inconvenience, so it is not a matter a customer's configuration can reach.
 *
 * `variation` is the documented exception and stays editable: an external party
 * legitimately approves variations, and its cost and margin fields are stripped
 * by `stripFinancialFields` rather than by the matrix.
 */
export function isForbiddenCell(role: Role, resource: Resource): boolean {
  if (!isExternalRole(role)) return false;
  return isFinancialResource(resource) && resource !== 'variation';
}

export type CellState = 'editable' | 'locked' | 'forbidden';

export function cellState(role: Role, resource: Resource, action: Action): CellState {
  if (isForbiddenCell(role, resource)) return 'forbidden';
  if (isLockedCell(role, resource, action)) return 'locked';
  return 'editable';
}

export type CustomiseFailure =
  | { readonly kind: 'unknown_resource'; readonly value: string }
  | { readonly kind: 'unknown_action'; readonly value: string }
  | { readonly kind: 'unknown_scope'; readonly value: string }
  | { readonly kind: 'locked_cell'; readonly resource: string; readonly action: string }
  | { readonly kind: 'forbidden_cell'; readonly resource: string }
  | { readonly kind: 'finance_on_external'; readonly resource: string };

/**
 * Check a proposed grant set for one role.
 *
 * Returns every problem rather than the first: an admin who has re-ticked a
 * whole role should be told everything that is wrong with it in one go, not
 * made to submit six times.
 *
 * Vocabulary validation is deliberately here as well as in the route schema.
 * The resources, actions and scopes ARE the model, and the model is still
 * code — a stored grant naming a resource that does not exist would be a row
 * that silently never matches anything, which is the worst kind of permission
 * bug because it looks like it is working.
 */
export function checkRoleGrants(
  role: Role,
  grants: RoleGrants,
  vocabulary: {
    readonly resources: readonly string[];
    readonly actions: readonly string[];
    readonly scopes: readonly string[];
  },
): readonly CustomiseFailure[] {
  const failures: CustomiseFailure[] = [];

  for (const [resource, list] of Object.entries(grants)) {
    if (!vocabulary.resources.includes(resource)) {
      failures.push({ kind: 'unknown_resource', value: resource });
      continue;
    }
    if (isForbiddenCell(role, resource as Resource)) {
      failures.push({ kind: 'forbidden_cell', resource });
      continue;
    }

    for (const grant of list ?? []) {
      if (!vocabulary.actions.includes(grant.action)) {
        failures.push({ kind: 'unknown_action', value: grant.action });
      }
      if (!vocabulary.scopes.includes(grant.scope)) {
        failures.push({ kind: 'unknown_scope', value: grant.scope });
      }
      // A finance-conditional grant on a role that can never hold finance:view
      // is a grant that can never fire. Storing it would be storing a lie about
      // what the role can do.
      if (grant.requiresFinance && isExternalRole(role)) {
        failures.push({ kind: 'finance_on_external', resource });
      }
    }
  }

  // Locked cells are checked against what is MISSING, which is the whole point
  // of them — the accident is an untick, not a tick.
  for (const [resource, actions] of Object.entries(LOCKED_CELLS)) {
    for (const action of actions) {
      if (!isLockedCell(role, resource as Resource, action)) continue;
      const held = (grants[resource as Resource] ?? []).some((g) => g.action === action);
      if (!held) failures.push({ kind: 'locked_cell', resource, action });
    }
  }

  return failures;
}

/** Does this role's proposed set differ from the shipped default at all? */
export function differsFromDefault(role: Role, grants: RoleGrants): boolean {
  return signature(grants) !== signature(PERMISSION_MATRIX[role]);
}

/**
 * A stable string for a grant set, for comparing one against another.
 *
 * Sorted, because two sets holding the same grants in a different order are
 * the same policy — and an admin who ticks a box and unticks it again should
 * not leave a company marked as customised.
 */
function signature(grants: RoleGrants): string {
  const rows: string[] = [];
  for (const [resource, list] of Object.entries(grants)) {
    for (const grant of (list ?? []) as readonly Grant[]) {
      rows.push(`${resource}:${grant.action}:${grant.scope}:${grant.requiresFinance ? 'f' : '-'}`);
    }
  }
  return rows.sort().join('|');
}
